import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getCaseStudyBySlug } from "../../data/caseStudies";

const SITE_URL = "https://www.obsidianweblabs.com";
const DEFAULT_TITLE = "Obsidian Web Labs — AI, Software & Web Development | Delhi, India";
const DEFAULT_DESCRIPTION =
  "Obsidian Web Labs builds AI systems, custom software, web experiences, and automation workflows for businesses worldwide.";

const exactMeta: Record<string, { title: string; description: string }> = {
  "/": {
    title: DEFAULT_TITLE,
    description:
      "Headquartered in Delhi, India. Enterprise AI and technology partner building CRM, ERP, websites, and automation systems for global businesses.",
  },
  "/about": {
    title: "About Obsidian Web Labs | Engineering-First AI & Software Partner",
    description:
      "Meet the team behind Obsidian Web Labs and our engineering-first approach to websites, software, AI systems, and business automation.",
  },
  "/blog": {
    title: "Blog & Insights | Obsidian Web Labs",
    description:
      "Read strategic notes, build insights, and practical thinking from Obsidian Web Labs across AI systems, software, growth, and digital operations.",
  },
  "/careers": {
    title: "Careers | Obsidian Web Labs",
    description:
      "Explore open roles at Obsidian Web Labs across engineering, AI, design, growth, and delivery.",
  },
  "/contact": {
    title: "Contact Obsidian Web Labs | Start Your Project",
    description:
      "Talk to Obsidian Web Labs about AI, software, automation, CRM, ERP, or web development. Reach our Delhi HQ team for a fast project consultation.",
  },
  "/pricing": {
    title: "Pricing | Obsidian Web Labs",
    description:
      "Explore transparent starting prices for websites, CRM, ERP, AI agents, call centre systems, SEO, e-commerce, and automation services.",
  },
  "/uae": {
    title: "UAE Technology Partner | Obsidian Web Labs",
    description:
      "Full-stack software, AI, and digital systems for UAE businesses with timezone-friendly delivery.",
  },
  "/uk": {
    title: "UK Software & AI Partner | Obsidian Web Labs",
    description:
      "Reliable software, AI, and digital delivery for UK businesses with clear communication.",
  },
  "/services": {
    title: "Services | Obsidian Web Labs",
    description:
      "Explore Obsidian Web Labs services across web development, CRM, ERP, AI agents, automation, marketing, and product engineering.",
  },
  "/work": {
    title: "Selected Work | Obsidian Web Labs",
    description:
      "See selected Obsidian Web Labs projects across AI systems, software platforms, websites, and business automation engagements.",
  },
  "/industries": {
    title: "Industries | Obsidian Web Labs",
    description:
      "Explore the industries Obsidian Web Labs serves with tailored CRM, ERP, AI, software, and digital systems.",
  },
  "/products": {
    title: "Products | Obsidian Web Labs",
    description:
      "Discover Obsidian Web Labs products, including industry-focused AI tools designed for legal and operational workflows.",
  },
  "/products/casemind": {
    title: "OWL CaseMind | AI Legal Research Product",
    description:
      "Learn about OWL CaseMind, the AI legal research assistant from Obsidian Web Labs for case search, summaries, and drafting support.",
  },
  "/india": {
    title: "Built in India | Obsidian Web Labs",
    description:
      "Obsidian Web Labs is an Indian technology firm headquartered in Delhi and serving businesses globally.",
  },
  "/india/mumbai": {
    title: "Mumbai Software & AI Development | Obsidian Web Labs",
    description:
      "Work with Obsidian Web Labs in Mumbai for AI systems, custom software, websites, CRM, ERP, and automation tailored to Indian businesses.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Obsidian Web Labs",
    description:
      "Read the Obsidian Web Labs privacy policy covering the information we collect, how we use it, and how to contact us about your data.",
  },
  "/terms-of-service": {
    title: "Terms of Service | Obsidian Web Labs",
    description:
      "Review the Obsidian Web Labs terms of service for website usage, project engagement expectations, and contact information.",
  },
};

const serviceLabels: Record<string, string> = {
  "web-development": "Web Development",
  "crm-development": "CRM Development",
  "erp-development": "ERP Development",
  "ai-agents": "AI Agents",
  "ai-call-centre": "AI Call Centre",
  "llm-integration": "LLM Integration",
  "rpa-automation": "RPA Automation",
  "app-development": "App Development",
  "digital-marketing": "Digital Marketing",
  "brand-strategy": "Brand Strategy",
  shopify: "Shopify Development",
  "custom-software": "Custom Software",
  "graphic-design": "Graphic Design",
  "content-writing": "Content Writing",
  wordpress: "WordPress Development",
  "creative-services": "Creative Services",
  "seo-services": "SEO Services",
  "digital-strategy": "Digital Strategy",
  "social-media": "Social Media Marketing",
  ecommerce: "E-Commerce Development",
  "ui-ux-design": "UI/UX Design",
  seo: "SEO & Organic Growth",
  branding: "Branding & Visual Identity",
  "web-apps": "Web Application Development",
  "mobile-apps": "Mobile App Development",
  "api-development": "Backend & API Development",
  "cloud-devops": "Cloud & DevOps",
  integrations: "Integrations & Automation",
  "rag-pipelines": "RAG Pipelines & Vector DBs",
  analytics: "Data, Analytics & BI",
  "ai-consulting": "AI Strategy Consulting",
};

const industryLabels: Record<string, string> = {
  legal: "Legal",
  "real-estate": "Real Estate",
  healthcare: "Healthcare",
  education: "Education",
  manufacturing: "Manufacturing",
};

const productLabels: Record<string, string> = {
  casemind: "OWL CaseMind",
  chatdesk: "OWL ChatDesk",
  callagent: "OWL CallAgent",
  crm: "OWL CRM",
  contentengine: "OWL ContentEngine",
  leadflow: "OWL LeadFlow",
  insightboard: "OWL InsightBoard",
  contractreader: "OWL ContractReader",
  reviewpulse: "OWL ReviewPulse",
};

const fallbackLabel = (segment: string) =>
  segment
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const setMetaTag = (selector: string, attribute: "name" | "property", key: string, content: string) => {
  let element = document.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const setCanonicalLink = (href: string) => {
  let link = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }

  link.setAttribute("href", href);
};

const resolveMeta = (pathname: string) => {
  if (exactMeta[pathname]) {
    return exactMeta[pathname];
  }

  if (pathname.startsWith("/services/")) {
    const slug = pathname.replace("/services/", "");
    const label = serviceLabels[slug] ?? fallbackLabel(slug);

    return {
      title: `${label} Services | Obsidian Web Labs`,
      description: `Obsidian Web Labs delivers ${label.toLowerCase()} for businesses that need measurable outcomes, better operations, and scalable digital systems.`,
    };
  }

  if (pathname.startsWith("/industries/")) {
    const slug = pathname.replace("/industries/", "");
    const label = industryLabels[slug] ?? fallbackLabel(slug);

    return {
      title: `${label} Industry Solutions | Obsidian Web Labs`,
      description: `See how Obsidian Web Labs builds software, AI, websites, and automation workflows tailored to ${label.toLowerCase()} teams.`,
    };
  }

  if (pathname.startsWith("/products/")) {
    const slug = pathname.replace("/products/", "");
    const label = productLabels[slug] ?? fallbackLabel(slug);

    return {
      title: `${label} | Obsidian Web Labs`,
      description: `Explore ${label} and other industry-focused software products built by Obsidian Web Labs.`,
    };
  }

  if (pathname.startsWith("/india/")) {
    const slug = pathname.replace("/india/", "");
    const label = fallbackLabel(slug);

    return {
      title: `${label} Technology Partner | Obsidian Web Labs`,
      description: `Work with Obsidian Web Labs in ${label} for software development, websites, AI systems, and business automation.`,
    };
  }

  if (pathname.startsWith("/work/")) {
    const slug = pathname.replace("/work/", "");
    const caseStudy = getCaseStudyBySlug(slug);

    if (caseStudy) {
      return {
        title: `${caseStudy.industry} Case Study | Obsidian Web Labs`,
        description: caseStudy.subtitle,
      };
    }

    return {
      title: "Selected Work | Obsidian Web Labs",
      description:
        "See selected Obsidian Web Labs projects across AI systems, software platforms, websites, and business automation engagements.",
    };
  }

  return {
    title: "Page Not Found | Obsidian Web Labs",
    description: DEFAULT_DESCRIPTION,
  };
};

const RouteEffects = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    const { title, description } = resolveMeta(location.pathname);
    const canonicalUrl = `${SITE_URL}${location.pathname === "/" ? "" : location.pathname}`;

    document.title = title;
    setMetaTag("meta[name='description']", "name", "description", description);
    setMetaTag("meta[property='og:title']", "property", "og:title", title);
    setMetaTag("meta[property='og:description']", "property", "og:description", description);
    setMetaTag("meta[property='og:url']", "property", "og:url", canonicalUrl);
    setMetaTag("meta[name='twitter:title']", "name", "twitter:title", title);
    setMetaTag("meta[name='twitter:description']", "name", "twitter:description", description);
    setMetaTag("meta[name='twitter:url']", "name", "twitter:url", canonicalUrl);
    setCanonicalLink(canonicalUrl);
  }, [location.pathname]);

  return null;
};

export default RouteEffects;
